class PressArticlesList extends HTMLElement {
  /** @type {number} */
  perPage;

  /** @type {number} */
  currentPage;

  /** @type {Element[]} */
  cards;

  /** @type {number} */
  totalPages;

  /** @type {Element | null} */
  grid;

  /** @type {Element | null} */
  paginationContainer;

  /** @type {HTMLButtonElement | null} */
  prevBtn;

  /** @type {HTMLButtonElement | null} */
  nextBtn;

  /** @type {HTMLElement | null} */
  paginationWrapper;

  constructor() {
    super();
    this.perPage = 9;
    this.currentPage = 1;
    this.cards = [];
    this.totalPages = 1;
    this.grid = null;
    this.paginationContainer = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.paginationWrapper = null;
  }

  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]') || this.querySelector('.press-page__grid');
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll('.press-page__item'));
    this.totalPages = Math.max(1, Math.ceil(this.cards.length / this.perPage));
    this.paginationContainer = this.querySelector('[data-pages]');
    this.prevBtn = this.querySelector('[data-action="prev"]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.paginationWrapper = this.querySelector('.press-page__pagination');

    // parse page from URL
    const params = new URLSearchParams(window.location.search);
    const pageParam = Number.parseInt(params.get('page') || '1', 10);
    if (!isNaN(pageParam) && pageParam >= 1 && pageParam <= this.totalPages) {
      this.currentPage = pageParam;
    }

    this._renderPage(this.currentPage);
    this._renderPagination();
    this._bind();
    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const p = Number.parseInt(params.get('page') || '1', 10) || 1;
      this._goToPage(p, { replaceState: true });
    });
  }

  _bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._goToPage(this.currentPage - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._goToPage(this.currentPage + 1));
    if (this.paginationContainer) {
      this.paginationContainer.addEventListener('click', (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        const btn = target.closest('[data-page]');
        if (!(btn instanceof HTMLButtonElement)) return;
        const p = Number(btn.getAttribute('data-page'));
        if (!Number.isNaN(p)) this._goToPage(p);
      });
    }
  }

  /** @param {number} page */
  _renderPage(page) {
    if (!Number.isFinite(page)) page = 1;
    this.cards.forEach((card, idx) => {
      const start = (page - 1) * this.perPage;
      const end = start + this.perPage;
      const element = card instanceof HTMLElement ? card : null;
      if (element) {
        if (idx >= start && idx < end) {
          element.style.display = '';
        } else {
          element.style.display = 'none';
        }
      }
    });

    // update prev/next disabled state
    if (this.prevBtn instanceof HTMLButtonElement) this.prevBtn.disabled = page <= 1;
    if (this.nextBtn instanceof HTMLButtonElement) this.nextBtn.disabled = page >= this.totalPages;
  }

  _renderPagination() {
    if (!this.paginationContainer) return;

    if (this.totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      if (this.paginationWrapper instanceof HTMLElement) this.paginationWrapper.style.display = 'none';
      return;
    }

    if (this.paginationWrapper instanceof HTMLElement) this.paginationWrapper.style.display = '';
    this.paginationContainer.innerHTML = '';

    // produce page buttons (limit to reasonable window if many pages)
    const maxButtons = 7;
    let start = 1;
    let end = this.totalPages;
    if (this.totalPages > maxButtons) {
      const side = Math.floor(maxButtons / 2);
      start = Math.max(1, this.currentPage - side);
      end = Math.min(this.totalPages, start + maxButtons - 1);
      if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);
    }

    for (let p = start; p <= end; p++) {
      const el = document.createElement(p === this.currentPage ? 'span' : 'button');
      el.className = 'press-page__pagination-number' + (p === this.currentPage ? ' press-page__pagination-number--current' : '');
      el.setAttribute('data-page', String(p));
      if (el instanceof HTMLButtonElement) {
        el.type = 'button';
      }
      el.textContent = String(p);
      this.paginationContainer.appendChild(el);
    }
  }

  /** @param {number} p */
  _goToPage(p, { replaceState = false } = {}) {
    if (!Number.isFinite(p)) p = 1;
    if (p < 1) p = 1;
    if (p > this.totalPages) p = this.totalPages;
    this.currentPage = p;
    this._renderPage(p);
    this._renderPagination();

    // update URL
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(p));
    if (replaceState) {
      history.replaceState({}, '', url.toString());
    } else {
      history.pushState({}, '', url.toString());
    }
  }
}

if (!customElements.get('press-articles-list')) {
  customElements.define('press-articles-list', PressArticlesList);
}
